import { useCallback } from 'react';
import { Account, Category } from '@/types';
import { toast } from 'sonner';

export const useCategoryDrop = (
  setCategories: React.Dispatch<React.SetStateAction<Category[]>>,
  removeAccount: (accountId: string) => void,
  addAccount: (account: Account) => void,
  accounts: Account[]
) => {
  const handleDrop = useCallback((accountId: string, categoryId: string) => {
    console.log('Handling drop:', { accountId, categoryId });
    
    setCategories((prevCategories) => {
      // First, find the account in the global accounts list
      const accountToMove = accounts.find(a => a.id === accountId);

      console.log('Account to move:', accountToMove);
      
      const updateCategoryAccounts = (categories: Category[]): Category[] => {
        return categories.map((cat) => {
          if (cat.id === categoryId) {
            // Remove the account from the uncategorized list
            removeAccount(accountId);
            
            // Find the account in the existing accounts array
            const existingAccount = cat.accounts?.find(a => a.id === accountId);
            
            if (!existingAccount && accountToMove) {
              console.log('Adding account to category:', { categoryId, account: accountToMove });
              
              // Remove account from other categories first
              const removeFromOtherCategories = (cats: Category[]): Category[] => {
                return cats.map((category) => ({
                  ...category,
                  accounts: (category.accounts || []).filter(a => a.id !== accountId),
                  children: removeFromOtherCategories(category.children)
                }));
              };

              // Clean up other categories first
              const updatedCategories = removeFromOtherCategories(prevCategories);
              
              // Now add the account to the target category
              const currentAccounts = Array.isArray(cat.accounts) ? cat.accounts : [];
              
              return {
                ...cat,
                accounts: [...currentAccounts, accountToMove]
              };
            }
          }
          
          // Recursively update children
          return {
            ...cat,
            children: updateCategoryAccounts(cat.children)
          };
        });
      };

      const newCategories = updateCategoryAccounts(prevCategories);
      console.log('Updated categories:', newCategories);
      return newCategories;
    });

    toast.success('Account categorized successfully');
  }, [setCategories, removeAccount, accounts]);

  const handleRemoveAccount = useCallback((accountId: string) => {
    let removedAccount: Account | undefined;

    setCategories((prevCategories) => {
      const removeAccountFromCategory = (categories: Category[]): Category[] => {
        return categories.map((cat) => {
          const foundAccount = cat.accounts?.find(acc => acc.id === accountId);
          if (foundAccount && !removedAccount) {
            removedAccount = foundAccount;
          }
          
          return {
            ...cat,
            accounts: (cat.accounts || []).filter(acc => acc.id !== accountId),
            children: removeAccountFromCategory(cat.children)
          };
        });
      };

      const updatedCategories = removeAccountFromCategory(prevCategories);
      
      if (removedAccount) {
        addAccount(removedAccount);
        console.log('Account removed and added back to uncategorized:', removedAccount);
      }

      return updatedCategories;
    });
  }, [setCategories, addAccount]);

  return {
    handleDrop,
    handleRemoveAccount,
  };
};